import { useState } from "react";
import type { CampaignEngagementItem } from "../../../api/campaigns";
import { useIsMobile } from "../../../lib/useMediaQuery";
import { GanttBar } from "./GanttBar";
import { type Zoom, dayColumns, nowLinePct, offHoursBands, windowBounds } from "./gantt-math";

interface Props {
  engagements: CampaignEngagementItem[];
  windowTz: string;
  businessDays: number[];
  poolSize: number;
  onSelect: (sortOrder: number) => void;
}

const ZOOM_MIN_WIDTH: Record<Zoom, number> = { day: 1800, "3day": 1100, week: 700 };
const LABEL_W_DESKTOP = 200;
const LABEL_W_MOBILE = 120;
const ROW_H = 48;

export function GanttChart({ engagements, windowTz, businessDays, poolSize, onSelect }: Props) {
  const isMobile = useIsMobile();
  const [zoom, setZoom] = useState<Zoom>(isMobile ? "week" : "3day");
  const LABEL_W = isMobile ? LABEL_W_MOBILE : LABEL_W_DESKTOP;
  // On a narrow track, thin the per-day axis labels so they don't collide.
  // week → label week-starts-ish (every 2nd day); 3day → every other day; day → all.
  const labelEvery = isMobile ? (zoom === "week" ? 2 : zoom === "3day" ? 2 : 1) : 1;
  const { start, end } = windowBounds(engagements);
  const spanMs = end.getTime() - start.getTime();
  const cols = dayColumns(start, end, zoom, labelEvery);
  const nowPct = nowLinePct(new Date(), start, spanMs);

  // V1 simplification: representative daily band from first engagement's hours
  const first = engagements[0];
  const dailyStart = first?.active_daily_start ?? "09:00";
  const dailyEnd = first?.active_daily_end ?? "18:00";
  const bands = offHoursBands(start, end, dailyStart, dailyEnd, businessDays, windowTz);

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-card)",
        background: "var(--bg-card)",
        overflow: "hidden",
        boxShadow: "var(--shadow-card)",
      }}
    >
      {/* zoom toggle */}
      <div
        style={{
          display: "flex",
          gap: 0,
          padding: "8px 12px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-card)",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-muted)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            marginRight: 10,
          }}
        >
          View:
        </span>
        <div
          style={{
            display: "inline-flex",
            border: "1px solid var(--border-strong)",
            background: "var(--bg-card)",
          }}
        >
          {(["day", "3day", "week"] as Zoom[]).map((z, i) => (
            <button
              key={z}
              onClick={() => setZoom(z)}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10.5,
                padding: "5px 11px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                border: "none",
                borderRight: i < 2 ? "1px solid var(--border-strong)" : "none",
                background: zoom === z ? "var(--text-primary)" : "transparent",
                color: zoom === z ? "var(--bg-card)" : "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      {/* scrollable timeline area */}
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${LABEL_W}px 1fr`,
            minWidth: LABEL_W + ZOOM_MIN_WIDTH[zoom],
          }}
        >
          {/* ── axis header ── */}
          <div
            style={{
              borderRight: "1px solid var(--border)",
              borderBottom: "1px solid var(--border)",
              background: "var(--bg-input)",
              height: 48,
              position: "sticky",
              left: 0,
              zIndex: 4,
            }}
          />
          <div
            style={{
              position: "relative",
              height: 48,
              borderBottom: "1px solid var(--border)",
              background: "var(--bg-input)",
            }}
          >
            {cols.map((c, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: `${c.leftPct}%`,
                  width: `${c.widthPct}%`,
                  top: 0,
                  bottom: 0,
                  borderRight: "1px solid var(--border)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-muted)",
                  padding: "12px 8px 0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  letterSpacing: "0.06em",
                }}
              >
                {c.showLabel ? c.label : ""}
              </div>
            ))}
          </div>

          {/* ── engagement rows ── */}
          {engagements.map((e) => (
            <GanttRow
              key={e.sort_order}
              engagement={e}
              windowStart={start}
              spanMs={spanMs}
              poolSize={poolSize}
              bands={bands}
              nowPct={nowPct}
              rowH={ROW_H}
              onSelect={onSelect}
            />
          ))}

          {/* empty state */}
          {engagements.length === 0 && (
            <>
              <div style={{ height: ROW_H, borderRight: "1px solid var(--border)" }} />
              <div
                style={{
                  height: ROW_H,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 16px",
                  color: "var(--text-muted)",
                  fontSize: 13,
                  fontFamily: "var(--font-sans)",
                }}
              >
                No engagements yet — add one below.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Per-row component ────────────────────────────────────────────────────────

interface RowProps {
  engagement: CampaignEngagementItem;
  windowStart: Date;
  spanMs: number;
  poolSize: number;
  bands: { leftPct: number; widthPct: number }[];
  nowPct: number | null;
  rowH: number;
  onSelect: (sortOrder: number) => void;
}

function GanttRow({ engagement: e, windowStart, spanMs, poolSize, bands, nowPct, rowH, onSelect }: RowProps) {
  return (
    <>
      {/* label rail */}
      <div
        style={{
          height: rowH,
          borderRight: "1px solid var(--border)",
          borderBottom: "1px solid var(--border-subtle)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 14px",
          background: "var(--bg-input)",
          overflow: "hidden",
          cursor: "pointer",
          position: "sticky",
          left: 0,
          zIndex: 4,
        }}
        onClick={() => onSelect(e.sort_order)}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--text-muted)",
            letterSpacing: "0.14em",
            marginBottom: 2,
          }}
        >
          #{String(e.sort_order + 1).padStart(2, "0")} / Engagement
        </div>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            fontWeight: 500,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {e.engagement_display_name}
        </div>
      </div>

      {/* track — bars + off-hours bands + NOW line live here */}
      <div
        style={{
          position: "relative",
          height: rowH,
          borderBottom: "1px solid var(--border-subtle)",
          overflow: "hidden",
        }}
      >
        {/* off-hours shading bands — behind bars, z-index 0 */}
        {bands.map((b, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${b.leftPct}%`,
              width: `${b.widthPct}%`,
              top: 0,
              bottom: 0,
              zIndex: 0,
              background:
                "repeating-linear-gradient(135deg, rgba(15,23,42,0.04) 0px, rgba(15,23,42,0.04) 3px, transparent 3px, transparent 7px)",
              pointerEvents: "none",
            }}
          />
        ))}

        {/* NOW line — thin vertical line at current time, z-index 3 */}
        {nowPct !== null && (
          <div
            style={{
              position: "absolute",
              left: `${nowPct}%`,
              top: 0,
              bottom: 0,
              width: 1.5,
              background: "var(--error)",
              zIndex: 3,
              pointerEvents: "none",
            }}
          >
            {/* diamond tick at top */}
            <div
              style={{
                position: "absolute",
                top: -1,
                left: -3,
                width: 7,
                height: 7,
                background: "var(--error)",
                transform: "rotate(45deg)",
              }}
            />
          </div>
        )}

        {/* engagement bar */}
        <GanttBar
          engagement={e}
          windowStart={windowStart}
          spanMs={spanMs}
          poolSize={poolSize}
          onSelect={onSelect}
        />
      </div>
    </>
  );
}
